import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { AxiosError } from 'axios'
import { submitDemoLead } from '../../../api'
import { useAuth } from '../core/Auth'
import { DEMO_CREDENTIALS, authModel, doLogin } from './Login'
import ButtonWithLoader from '../../widgets/components/ButtonWithLoader'
import { brandSettings } from '../../settings/BrandSettings'

const RECAPTCHA_SITE_KEY =
  import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY || '6Lfe2qcqAAAAADYv7LKlI60-npVxtljZiepSXLyY'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Long enough for the thank-you notice to be read before the demo loads
const THANK_YOU_MS = 2500

type Step = 'form' | 'thanks'

export function DemoLanding() {
  const navigate = useNavigate()
  const { saveAuth, saveCurrentUser } = useAuth()
  const captchaRef = useRef<ReCAPTCHA>(null)

  const [step, setStep] = useState<Step>('form')
  const [email, setEmail] = useState('')
  // Pre-ticked by request; the visitor can untick it before submitting
  const [marketingOptIn, setMarketingOptIn] = useState(true)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const emailValid = EMAIL_PATTERN.test(email.trim())

  const loadDemo = async () => {
    const loginResponse = await doLogin({ ...DEMO_CREDENTIALS })
    if (!loginResponse) {
      setError('We could not open the demo automatically. Please sign in from the login page.'.toTranslatedDisplay())
      return
    }
    authModel.api_token = loginResponse.token
    saveAuth(authModel)
    saveCurrentUser(loginResponse.user)
    navigate('/dashboard', { replace: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!emailValid || !captchaToken || submitting) return
    setSubmitting(true)
    setError('')
    try {
      await submitDemoLead(email.trim(), captchaToken, marketingOptIn)
      setStep('thanks')
      await new Promise(resolve => setTimeout(resolve, THANK_YOU_MS))
      await loadDemo()
    } catch (err) {
      // A captcha token is single-use, so always make the visitor solve a fresh one
      captchaRef.current?.reset()
      setCaptchaToken(null)
      setStep('form')
      const message = (err as AxiosError<{ message?: string | string[] }>).response?.data?.message
      setError(
        (Array.isArray(message) ? message[0] : message) ||
          'Something went wrong. Please try again.'.toTranslatedDisplay()
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'thanks') {
    return (
      <div className='text-center w-100'>
        <h1 className='text-gray-900 fw-bolder mb-3'>{'Thank you!'.toTranslatedDisplay()}</h1>
        <div className='text-gray-600 fw-semibold fs-5 mb-8'>
          {'Your demo is loading. This takes a few seconds.'.toTranslatedDisplay()}
        </div>
        <span className='spinner-border text-primary' role='status' />
        {error && (
          <div className='alert alert-danger mt-8'>
            <div className='alert-text'>{error}</div>
            <Link to='/auth/login' className='fw-bold'>{'Go to login'.toTranslatedDisplay()}</Link>
          </div>
        )}
      </div>
    )
  }

  return (
    <form className='form w-100' onSubmit={handleSubmit} noValidate id='kt_demo_lead_form'>
      <div className='mb-8'>
        <h1 className='text-gray-900 fw-bolder mb-3'>
          {`${'Try'.toTranslatedDisplay()} ${brandSettings.displayName} ${'free demo'.toTranslatedDisplay()}`}
        </h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {'Enter your email to explore a fully loaded demo business. No sign-up needed.'.toTranslatedDisplay()}
        </div>
      </div>

      {error && (
        <div className='mb-5 alert alert-danger'>
          <div className='alert-text fw-bold'>{error}</div>
        </div>
      )}

      <div className='fv-row mb-6'>
        <label className='form-label fs-6 fw-bolder text-gray-900' htmlFor='demo_email'>
          {'Email'.toTranslatedDisplay()}
        </label>
        <input
          id='demo_email'
          type='email'
          autoComplete='email'
          placeholder={'you@company.com'.toTranslatedDisplay()}
          className='form-control bg-transparent'
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
      </div>

      <div className='form-check form-check-custom form-check-solid mb-6'>
        <input
          id='demo_marketing_opt_in'
          className='form-check-input'
          type='checkbox'
          checked={marketingOptIn}
          onChange={e => setMarketingOptIn(e.target.checked)}
        />
        <label className='form-check-label text-gray-700' htmlFor='demo_marketing_opt_in'>
          {'Send me product news and tips. Optional, and you can unsubscribe at any time.'.toTranslatedDisplay()}
        </label>
      </div>

      <div className='mb-6'>
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={token => setCaptchaToken(token)}
          onExpired={() => setCaptchaToken(null)}
        />
      </div>

      <ButtonWithLoader
        type='submit'
        onClick={() => undefined} // submission is handled by the form's onSubmit
        isLoading={submitting}
        disabled={!emailValid || !captchaToken}
        className='btn btn-primary w-100'
      >
        {'Start the demo'.toTranslatedDisplay()}
      </ButtonWithLoader>

      <div className='text-gray-500 fs-7 mt-5'>
        {'This is a shared demo account. Please do not store personal information.'.toTranslatedDisplay()}{' '}
        <Link to='/auth/login'>{'Back to login'.toTranslatedDisplay()}</Link>
      </div>
    </form>
  )
}
